/**
 * In-memory bearer-token bridge for hosts that deliver a MyProviderData API
 * token over postMessage (PBS Live desktop/mobile) instead of this iframe
 * running its own MSAL flow. Mirrors the PBS calendar embed protocol:
 *
 *   child -> host  { type: 'pbs-mpd:ready' }           announce, ask for a token
 *   host  -> child { type: 'pbs-mpd:token', token }    deliver / refresh the token
 *   child -> host  { type: 'pbs-mpd:token-expired' }   current token rejected or expiring
 *
 * The token only ever lives in this module's memory - never in a URL, never in
 * storage. Hosts that don't implement the protocol simply never answer, and the
 * caller falls back to MSAL (see EmbeddedMsalSsoGate).
 */

const READY_TIMEOUT_MS = 1500;
const REFRESH_TIMEOUT_MS = 15000;
const EXPIRY_LEEWAY_MS = 60_000;

let token: string | null = null;
let expiresAtMs = 0;
let bridgeActive = false;
let listening = false;
let pendingRefresh: Promise<string | null> | null = null;
let tokenListeners: Array<(t: string) => void> = [];

const readExpiryMs = (jwt: string): number => {
  try {
    const payload = JSON.parse(
      atob(jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    // No exp claim -> assume a short lifetime so we refresh eagerly.
    return payload.exp ? payload.exp * 1000 : Date.now() + 5 * 60_000;
  } catch {
    return Date.now() + 5 * 60_000;
  }
};

const setToken = (value: string) => {
  token = value;
  expiresAtMs = readExpiryMs(value);
  bridgeActive = true;
  const listeners = tokenListeners;
  tokenListeners = [];
  listeners.forEach((listener) => listener(value));
};

const ensureListening = () => {
  if (listening) return;
  listening = true;
  window.addEventListener("message", (event: MessageEvent) => {
    // Only the embedding host may deliver tokens. The host's origin isn't
    // knowable up front (Electron renderers, webviews), so pin the source
    // window instead - nothing but the direct parent is accepted.
    if (event.source !== window.parent) return;
    const data = event.data;
    if (!data || data.type !== "pbs-mpd:token" || typeof data.token !== "string") return;
    setToken(data.token);
  });
};

const postToHost = (message: { type: string }) => {
  // The ready/expired messages carry no data worth protecting, and the host
  // origin is unknown, so a wildcard target is safe here.
  window.parent.postMessage(message, "*");
};

const isEmbedded = () => window.parent !== window;

/** True once a host has delivered at least one token this page load. */
export const isBridgeActive = (): boolean => bridgeActive;

/**
 * Announces this embed to the host and waits briefly for the first token.
 * Resolves null when not embedded or when no host answers in time - callers
 * then fall back to MSAL. A token that arrives late is still accepted and
 * used for subsequent API calls.
 */
export const waitForBridgeToken = (): Promise<string | null> => {
  if (!isEmbedded()) return Promise.resolve(null);
  if (token) return Promise.resolve(token);

  ensureListening();

  return new Promise<string | null>((resolve) => {
    const timeout = window.setTimeout(() => resolve(null), READY_TIMEOUT_MS);
    tokenListeners.push((t) => {
      window.clearTimeout(timeout);
      resolve(t);
    });
    postToHost({ type: "pbs-mpd:ready" });
  });
};

/**
 * Returns a valid bridged token, asking the host for a refresh when the
 * current one is missing or about to expire. Concurrent callers share a
 * single refresh request. Resolves null when the host stops answering.
 */
export const getBridgeToken = (): Promise<string | null> => {
  if (token && Date.now() < expiresAtMs - EXPIRY_LEEWAY_MS) {
    return Promise.resolve(token);
  }
  if (!pendingRefresh) {
    pendingRefresh = requestRefresh().finally(() => {
      pendingRefresh = null;
    });
  }
  return pendingRefresh;
};

/** Drops the cached token (e.g. after a 401) so the next call refreshes. */
export const invalidateBridgeToken = (): void => {
  token = null;
  expiresAtMs = 0;
};

const requestRefresh = (): Promise<string | null> => {
  ensureListening();
  return new Promise<string | null>((resolve) => {
    const timeout = window.setTimeout(() => resolve(null), REFRESH_TIMEOUT_MS);
    tokenListeners.push((t) => {
      window.clearTimeout(timeout);
      resolve(t);
    });
    postToHost({ type: "pbs-mpd:token-expired" });
  });
};
